import React, { useEffect, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { patientActions } from "../../core/actions";
import { ROLE_KEYS } from "../../shared/constants";
import Components from "..";
import "./StandaloneProcedureListing.css";

const hasRole = (expectedRole) => {
  const storedRole = localStorage.getItem("role");
  if (!storedRole) return false;

  let role = storedRole;
  try {
    role = JSON.parse(storedRole);
  } catch (error) {
    // Keep the plain string value.
  }

  const roles = Array.isArray(role) ? role : [role];
  return roles.some(
    (item) =>
      String(item).trim().toLowerCase() === expectedRole.toLowerCase(),
  );
};

const formatPersonName = (person) =>
  person?.fullName ||
  [
    person?.displaytitle || person?.title,
    person?.first_name || person?.firstName || person?.fname,
    person?.last_name || person?.lastName || person?.lname,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

const getDepartmentName = (person) => {
  const department = person?.department;
  if (typeof department === "string") return department;

  return (
    department?.name ||
    department?.departmentName ||
    person?.departmentName ||
    person?.deptname ||
    person?.specialization ||
    ""
  );
};

const getAssignedDoctor = (visitResponse) => {
  const visitRecord = visitResponse?.patsearchreslt?.[0];
  if (!visitRecord) return {};

  const visit = visitRecord.visit || visitRecord;
  const assignments =
    visitRecord.visitAssignmentList || visit?.visitAssignmentList || [];
  const assignment = [...assignments].reverse().find((item) => {
    const group = String(item?.groupname || "").toLowerCase();
    const type = String(item?.type || "").toLowerCase();
    const isDoctor =
      group === "primary doctor" ||
      group === "new session" ||
      (!group && (!type || type === "doctor"));

    return item?.targetid && item?.status === "open" && isDoctor;
  });
  const people = visitResponse?.personList || visitRecord?.personList || [];
  const person = assignment
    ? people.find(
        (item) =>
          String(item?.personId || item?.id || item?.gvId) ===
          String(assignment.targetid),
      )
    : null;
  const fallbackPerson = visit?.person || visitRecord?.person;
  const assignedPerson = person || fallbackPerson;

  return {
    person: assignedPerson,
    doctorName:
      formatPersonName(assignedPerson) || assignment?.targetname || "",
    department: getDepartmentName(assignedPerson),
    visit,
  };
};

function StandaloneProcedureListing() {
  const { patientId, visitId } = useParams();
  const dispatch = useDispatch();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    let profileData = null;
    let procedureData = null;
    let visitData = null;
    let profileRequestComplete = false;
    let visitRequestComplete = false;
    setLoading(true);
    setError("");

    const mergePatientData = (profile, procedure, visitResponse) => {
      const assignedDoctor = getAssignedDoctor(visitResponse);

      return {
        ...(profile || {}),
        ...(procedure || {}),
        person:
          assignedDoctor.person || procedure?.person || profile?.person,
        doctor:
          assignedDoctor.person || procedure?.doctor || profile?.doctor,
        doctorName:
          assignedDoctor.doctorName ||
          procedure?.doctorName ||
          profile?.doctorName,
        department:
          assignedDoctor.department ||
          procedure?.department ||
          profile?.department,
        visit: {
          ...(profile?.visit || {}),
          ...(assignedDoctor.visit || {}),
          ...(procedure?.visit || {}),
          person:
            assignedDoctor.person ||
            procedure?.visit?.person ||
            profile?.visit?.person,
          department:
            assignedDoctor.department ||
            procedure?.visit?.department ||
            profile?.visit?.department,
        },
        visitList: profile?.visitList || procedure?.visitList || [],
      };
    };

    const showPatient = () => {
      if (
        !mounted ||
        !profileRequestComplete ||
        !visitRequestComplete ||
        !procedureData
      )
        return;

      const mergedPatient = mergePatientData(
        profileData,
        procedureData,
        visitData,
      );
      setPatient(mergedPatient);
      dispatch(patientActions.setPatientDetails(mergedPatient));
      setLoading(false);
    };

    // Procedure data does not include the patient's complete visit list.
    // Load and merge the profile so visit filters and print headers have the
    // same patient, doctor, and visit context as the main procedure listing.
    dispatch(
      patientActions.getPatientProfile(patientId, (profile) => {
        if (!mounted) return;

        profileData = profile?.id ? profile : null;
        profileRequestComplete = true;
        showPatient();
      }),
    );

    dispatch(
      patientActions.getPatientProcedureInfo(
        patientId,
        visitId,
        "showAllVisits",
        (response) => {
          if (!mounted) return;

          if (!response?.patientresp) {
            setError("Unable to load the patient's procedure list.");
            setLoading(false);
            return;
          }

          procedureData = response.patientresp;
          showPatient();
        },
      ),
    );

    dispatch(
      patientActions.getVisitListById(
        visitId,
        "showAllVisits",
        (response) => {
          if (!mounted) return;

          visitData = response;
          visitRequestComplete = true;
          showPatient();
        },
      ),
    );

    return () => {
      mounted = false;
    };
  }, [dispatch, patientId, visitId]);

  if (loading) {
    return (
      <div className="standalone-procedure-loading">
        <CircularProgress size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  const isLabAdmin = hasRole(ROLE_KEYS.LabAdmin);
  const isMedicalOfficer = hasRole(ROLE_KEYS.MedicalOfficer);
  const isRadiology = hasRole(ROLE_KEYS.Ultrasound);
  const isXRay = hasRole(ROLE_KEYS.XRay);

  return (
    <main className="standalone-procedure-page h-100" id="maincontainer_1">
      <div className="standalone-procedure-panel h-100">
        <Components.LayoutHeader
          basic={false}
          lab_admin={isLabAdmin}
          medicalofficer={isMedicalOfficer}
          radiology={isRadiology}
          x_ray={isXRay}
          selectedPatient={patient}
          onPatientBasic={() => {}}
          onVisitCompletion={() => {}}
          readOnly
        />

        <Components.ProcedureListing
          basic={false}
          lab_admin={isLabAdmin}
          medicalofficer={isMedicalOfficer}
          radiology={isRadiology}
          x_ray={isXRay}
          docList={[]}
          onPatientBasic={() => {}}
          selectedPatientInfo={patient}
          readOnly
          allowPrint
          painAssessmentConsentOnly
        />
      </div>
    </main>
  );
}

export default StandaloneProcedureListing;
